import type { Id, ProductWithVariants } from "@estelle/domain";
import { ImageOff } from "lucide-react";
import type { ReactNode } from "react";

import { renderCell } from "@/components/table/cells";
import { ListWithFooter } from "@/components/table/ListFooter";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { CATALOG_FIELDS } from "@/modules/catalog/fields";
import type { ProductRow } from "@/modules/catalog/projections";
import {
  PriceSummary,
  ProductTags,
  RatingStars,
} from "@/modules/catalog/screens/ProductSummary";

const STATUS_FIELD = CATALOG_FIELDS.find((field) => field.name === "status");

export interface ProductCardsProps {
  rows: ProductRow[];
  detailById: Map<Id, ProductWithVariants | null>;
  selected: Set<Id>;
  onSelectedChange: (id: Id, checked: boolean) => void;
  onOpen: (id: Id) => void;
  emptyState: ReactNode;
  /** The pager, placed the same way `DataTable`'s `footer` is (#382). */
  footer?: ReactNode;
}

function Image({
  product,
  className,
}: {
  product: ProductRow;
  className: string;
}) {
  // Straight from Shopify's CDN, as the table's thumbnail (FR-CAT-13).
  return product.featuredImageUrl ? (
    <img
      src={product.featuredImageUrl}
      alt=""
      loading="lazy"
      className={cn("bg-muted object-cover", className)}
    />
  ) : (
    <div
      className={cn(
        "bg-muted text-muted-foreground flex items-center justify-center",
        className,
      )}
      aria-hidden
    >
      <ImageOff className="size-6" />
    </div>
  );
}

function HsnMissing({ product }: { product: ProductRow }) {
  if (product.variantCount === 0 || product.hsnMissingCount === 0) return null;
  return (
    <Badge variant="destructive" className="font-normal">
      HSN missing
    </Badge>
  );
}

/**
 * The card's whole surface opens the product, and the checkbox beside it
 * selects it.
 *
 * A button stretched over the card with `after:inset-0` rather than a click
 * handler on the card: the card holds a checkbox, a button cannot contain one,
 * and a clickable `div` is invisible to the keyboard. The checkbox sits above
 * the stretched area so ticking it never also opens the drawer.
 */
function OpenButton({
  product,
  onOpen,
  className,
}: {
  product: ProductRow;
  onOpen: (id: Id) => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        onOpen(product.id);
      }}
      className={cn(
        "text-left font-medium after:absolute after:inset-0 after:rounded-lg focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring",
        className,
      )}
    >
      {product.title}
    </button>
  );
}

function SelectBox({
  product,
  checked,
  onCheckedChange,
  className,
}: {
  product: ProductRow;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  className?: string;
}) {
  return (
    <Checkbox
      checked={checked}
      onCheckedChange={(value) => {
        onCheckedChange(value === true);
      }}
      aria-label={`Select ${product.title}`}
      className={cn("relative z-10", className)}
    />
  );
}

function Scroller({
  footer,
  children,
}: {
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <ListWithFooter footer={footer} scrollClassName="p-4">
      {children}
    </ListWithFooter>
  );
}

/**
 * Products as a grid of cards — image first, for judging how the catalog
 * looks: a missing photo, a mismatched crop, a price that reads wrong.
 */
export function ProductGrid({
  rows,
  detailById,
  selected,
  onSelectedChange,
  onOpen,
  emptyState,
  footer,
}: ProductCardsProps) {
  if (rows.length === 0) return <>{emptyState}</>;

  return (
    <Scroller footer={footer}>
      <ul className="grid grid-cols-[repeat(auto-fill,minmax(13rem,1fr))] gap-4">
        {rows.map((product) => {
          const detail = detailById.get(product.id);
          const checked = selected.has(product.id);
          return (
            <li
              key={product.id}
              className={cn(
                "bg-card hover:border-primary/60 relative flex flex-col overflow-hidden rounded-lg border transition-colors",
                checked && "border-primary ring-primary ring-1",
              )}
            >
              <div className="relative">
                <Image product={product} className="aspect-square w-full" />
                <SelectBox
                  product={product}
                  checked={checked}
                  onCheckedChange={(next) => {
                    onSelectedChange(product.id, next);
                  }}
                  className="bg-background border-foreground/30 absolute top-2 left-2 shadow-sm"
                />
                {product.status !== "active" && STATUS_FIELD ? (
                  <div className="absolute top-2 right-2">
                    {renderCell(STATUS_FIELD, product.status)}
                  </div>
                ) : null}
              </div>
              <div className="flex flex-1 flex-col gap-1.5 p-3">
                <OpenButton
                  product={product}
                  onOpen={onOpen}
                  className="line-clamp-2 text-sm"
                />
                <p className="text-muted-foreground truncate text-xs">
                  {[product.vendor, product.productType]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </p>
                <RatingStars metafields={product.metafields} size="sm" />
                <div className="mt-auto flex items-end justify-between gap-2 pt-1">
                  <PriceSummary variants={detail?.variants ?? []} size="sm" />
                  <HsnMissing product={product} />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </Scroller>
  );
}

/**
 * Products as tiles — a short card with the image on the left, carrying tags
 * and HSN coverage the grid has no room for. More per product than the grid,
 * faster to scan than the table.
 */
export function ProductTiles({
  rows,
  detailById,
  selected,
  onSelectedChange,
  onOpen,
  emptyState,
  footer,
}: ProductCardsProps) {
  if (rows.length === 0) return <>{emptyState}</>;

  return (
    <Scroller footer={footer}>
      <ul className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,24rem),1fr))] gap-3">
        {rows.map((product) => {
          const detail = detailById.get(product.id);
          const checked = selected.has(product.id);
          return (
            <li
              key={product.id}
              className={cn(
                "bg-card hover:border-primary/60 relative flex items-center gap-3 rounded-lg border p-3 transition-colors",
                checked && "border-primary ring-primary ring-1",
              )}
            >
              <SelectBox
                product={product}
                checked={checked}
                onCheckedChange={(next) => {
                  onSelectedChange(product.id, next);
                }}
              />
              <Image
                product={product}
                className="size-20 shrink-0 rounded-md"
              />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex items-start justify-between gap-2">
                  <OpenButton
                    product={product}
                    onOpen={onOpen}
                    className="line-clamp-1 text-sm"
                  />
                  {STATUS_FIELD ? (
                    <span className="shrink-0">
                      {renderCell(STATUS_FIELD, product.status)}
                    </span>
                  ) : null}
                </div>
                <p className="text-muted-foreground truncate text-xs">
                  {[product.vendor, product.productType]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </p>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <PriceSummary variants={detail?.variants ?? []} size="sm" />
                  <RatingStars metafields={product.metafields} size="sm" />
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  <ProductTags tags={product.tags} />
                  <HsnMissing product={product} />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </Scroller>
  );
}
